import React from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';

const Filters = ({ 
  filters, 
  setFilters, 
  taxonomy, 
  onClear 
}) => {
  // Keep the taxonomy's order (Calm, Happy, ...) but offer only moods in use.
  const moodsInUse = taxonomy?.mood_categories_in_use || [];
  const moodOptions = (taxonomy?.mood_categories || []).filter(mood => moodsInUse.includes(mood));
  const brightMoods = new Set(['Happy', 'Energetic', 'Playful', 'Hopeful', 'Confident', 'Chill', 'Warm']);
  const moodyMoods = new Set(['Sad', 'Dark', 'Reflective', 'Tense', 'Angry', 'Calm', 'Focused']);
  // Do not offer a brightness filter unless at least one published loop can match it.
  const vibeOptions = (taxonomy?.vibes || []).filter(vibe => (
    vibe === 'Bright'
      ? moodsInUse.some(mood => brightMoods.has(mood))
      : vibe === 'Moody' && moodsInUse.some(mood => moodyMoods.has(mood))
  ));
  const energyInUse = taxonomy?.energy_levels_in_use || [];
  // The keys the catalog stores, split by quality. Written "C min" / "A maj"
  // here, so the test is on the suffix rather than the word "Minor".
  const keyOptions = taxonomy?.musical_keys_in_use?.length
    ? taxonomy.musical_keys_in_use
    : taxonomy?.musical_keys || [];
  const majorKeys = keyOptions.filter(k => /maj/i.test(k));
  const minorKeys = keyOptions.filter(k => /min/i.test(k));

  const hasActiveFilters = Object.values(filters).some(v => 
    v !== '' && v !== null && v !== undefined && 
    !(Array.isArray(v) && v.length === 0) &&
    !(Array.isArray(v) && v[0] === 60 && v[1] === 200)
  );
  
  return (
    <div data-testid="filters-panel" className="space-y-6">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          data-testid="search-input"
          type="text"
          placeholder="Search loops..."
          value={filters.search || ''}
          onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          className="pl-12 h-12 rounded-xl bg-muted/50 border-transparent focus:border-primary"
        />
      </div>
      
      {/* Mood Category: only moods some published loop actually carries */}
      {moodOptions.length > 0 && <div>
        <label className="block text-sm font-semibold mb-2 text-muted-foreground">Mood</label>
        <div className="flex flex-wrap gap-2">
          {moodOptions.map(mood => (
            <Button
              key={mood}
              data-testid={`mood-filter-${mood}`}
              variant={filters.mood_category === mood ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilters(prev => ({
                ...prev,
                mood_category: prev.mood_category === mood ? '' : mood
              }))}
              className={`rounded-full ${
                filters.mood_category === mood 
                  ? 'bg-primary text-black hover:bg-primary/90' 
                  : 'border-white/20 hover:bg-white/10'
              }`}
            >
              {mood}
            </Button>
          ))}
        </div>
      </div>}
      
      {/* Vibe: Bright/Moody only appear when the catalog has a matching mood. */}
      {vibeOptions.length > 0 && <div>
        <label className="block text-sm font-semibold mb-2 text-muted-foreground">Vibe</label>
        <div className="flex gap-2">
          {vibeOptions.map(vibe => (
            <Button
              key={vibe}
              data-testid={`vibe-filter-${vibe}`}
              variant={filters.vibe === vibe ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilters(prev => ({
                ...prev,
                vibe: prev.vibe === vibe ? '' : vibe
              }))}
              className={`rounded-full flex-1 ${
                filters.vibe === vibe 
                  ? vibe === 'Bright' ? 'bg-lime text-black' : 'bg-violet text-white'
                  : 'border-white/20 hover:bg-white/10'
              }`}
            >
              {vibe}
            </Button>
          ))}
        </div>
      </div>}
      
      {/* Style/Genre */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-muted-foreground">Style</label>
        <Select
          value={filters.style_tags || 'all'}
          onValueChange={(value) => setFilters(prev => ({ ...prev, style_tags: value === 'all' ? '' : value }))}
        >
          <SelectTrigger data-testid="style-select" className="rounded-xl bg-muted/50 border-transparent">
            <SelectValue placeholder="Any style" />
          </SelectTrigger>
          <SelectContent>
            {/* Driven by what the catalog contains, not the tagging vocabulary.
                Ten of the eighteen vocabulary styles match nothing here, and
                five styles loops really carry were not offered at all. */}
            <SelectItem value="all">Any style</SelectItem>
            {(taxonomy?.style_tags_in_use?.length ? taxonomy.style_tags_in_use : taxonomy?.style_tags || []).map(style => (
              <SelectItem key={style} value={style}>{style}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Musical Key (for matching melodies).
          Offers the keys the catalog stores, which are written "C min" and
          "A maj". The vocabulary list spells them "C Minor" and "A Major", and
          there was no overlap at all between the two, so every key a student
          could pick returned an empty library. */}
      {keyOptions.length > 0 && (
        <div>
          <label className="block text-sm font-semibold mb-2 text-muted-foreground">Key</label>
          <Select
            value={filters.key || 'all'}
            onValueChange={(value) => setFilters(prev => ({ ...prev, key: value === 'all' ? '' : value }))}
          >
            <SelectTrigger data-testid="key-select" className="rounded-xl bg-muted/50 border-transparent">
              <SelectValue placeholder="Any key" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any key</SelectItem>
              {majorKeys.length > 0 && (
                <div className="px-2 py-1 text-xs text-muted-foreground font-semibold">Major Keys</div>
              )}
              {majorKeys.map(key => (
                <SelectItem key={key} value={key}>{key}</SelectItem>
              ))}
              {minorKeys.length > 0 && (
                <div className="px-2 py-1 text-xs text-muted-foreground font-semibold mt-1">Minor Keys</div>
              )}
              {minorKeys.map(key => (
                <SelectItem key={key} value={key}>{key}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {/* BPM/Tempo Filter - Preset buttons + Fine-tune slider */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-muted-foreground">
          Tempo: {filters.bpm_range?.[0] === filters.bpm_range?.[1] 
            ? `${filters.bpm_range?.[0] || 'All'} BPM` 
            : `${filters.bpm_range?.[0] || 60} - ${filters.bpm_range?.[1] || 200} BPM`}
        </label>
        
        {/* Quick BPM Presets */}
        <div className="flex flex-wrap gap-2 mb-3">
          {[70, 80, 90, 100, 110, 120, 130, 140, 150, 160, 170, 180, 190, 200].map(bpm => (
            <Button
              key={bpm}
              data-testid={`bpm-preset-${bpm}`}
              variant={filters.bpm_range?.[0] === bpm && filters.bpm_range?.[1] === bpm ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                // If already selected, show all BPMs
                if (filters.bpm_range?.[0] === bpm && filters.bpm_range?.[1] === bpm) {
                  setFilters(prev => ({ ...prev, bpm_range: [60, 200] }));
                } else {
                  // Filter to just this BPM (±5 tolerance)
                  setFilters(prev => ({ ...prev, bpm_range: [bpm, bpm] }));
                }
              }}
              className={`rounded-full text-xs h-7 px-3 ${
                filters.bpm_range?.[0] === bpm && filters.bpm_range?.[1] === bpm 
                  ? 'bg-primary text-black hover:bg-primary/90' 
                  : 'border-white/20 hover:bg-white/10'
              }`}
            >
              {bpm}
            </Button>
          ))}
          <Button
            data-testid="bpm-preset-all"
            variant={filters.bpm_range?.[0] === 60 && filters.bpm_range?.[1] === 200 ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilters(prev => ({ ...prev, bpm_range: [60, 200] }))}
            className={`rounded-full text-xs h-7 px-3 ${
              filters.bpm_range?.[0] === 60 && filters.bpm_range?.[1] === 200 
                ? 'bg-primary text-black hover:bg-primary/90' 
                : 'border-white/20 hover:bg-white/10'
            }`}
          >
            All
          </Button>
        </div>
        
        {/* Range Slider for fine-tuning */}
        <div className="px-1">
          <Slider
            data-testid="bpm-slider"
            value={filters.bpm_range || [60, 200]}
            onValueChange={(value) => setFilters(prev => ({ ...prev, bpm_range: value }))}
            min={60}
            max={200}
            step={5}
            className="py-2"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>60</span>
            <span>200</span>
          </div>
        </div>
      </div>
      
      {/* Energy: hidden until loops are tagged; untagged levels are disabled */}
      {energyInUse.length > 0 && <div>
        <label className="block text-sm font-semibold mb-2 text-muted-foreground">Energy Level</label>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map(level => (
            <Button
              key={level}
              data-testid={`energy-filter-${level}`}
              variant="ghost"
              size="sm"
              disabled={!energyInUse.includes(level)}
              aria-label={`Energy ${level}`}
              onClick={() => setFilters(prev => ({
                ...prev,
                energy: prev.energy === level ? null : level
              }))}
              className={`flex-1 h-10 rounded-lg p-0 transition-all ${
                filters.energy === level 
                  ? 'bg-accent hover:bg-accent' 
                  : filters.energy && level <= filters.energy
                    ? 'bg-accent/50 hover:bg-accent/60'
                    : 'bg-white/10 hover:bg-white/20'
              }`}
            />
          ))}
        </div>
      </div>}
      
      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button
          data-testid="clear-filters-btn"
          onClick={onClear}
          variant="ghost"
          className="w-full rounded-full text-muted-foreground hover:text-white"
        >
          <X className="w-4 h-4 mr-2" />
          Clear all filters
        </Button>
      )}
    </div>
  );
};

export default Filters;
